import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, ChevronDown, Download, Printer, Search, X } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { DayTabs } from "@/components/fly/DayTabs";
import { FilterSelect } from "@/components/fly/FilterSelect";

const DAY_KEY = "flycup_reg_day";
const STATUS = ["Wszyscy", "Nieobecni", "Obecni", "Bez ubezpieczenia"];

export const RegistrationBoard = () => {
  const [days, setDays] = useState([]);
  const [day, setDay] = useState(localStorage.getItem(DAY_KEY) || "");
  const [data, setData] = useState(null);
  const [club, setClub] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/config").then((r) => {
      setDays(r.data.days);
      if (!r.data.days.includes(day)) setDay(r.data.days[0]);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!day) return;
    localStorage.setItem(DAY_KEY, day);
    setClub("");
    api.get("/registration", { params: { day } }).then((r) => setData(r.data));
  }, [day]);

  const update = async (person, patch) => {
    try {
      const { data: res } = await api.put("/registration/checkin", { name: person.name, school: person.school, day, ...patch });
      setData((d) => {
        const clubs = d.clubs.map((c) => {
          const persons = c.persons.map((p) => (p.name === res.name && p.school === res.school ? { ...p, ...res } : p));
          return { ...c, persons, present: persons.filter((p) => p.present).length, insured: persons.filter((p) => p.insured).length };
        });
        return { ...d, clubs, present: clubs.reduce((s, c) => s + c.present, 0), insured: clubs.reduce((s, c) => s + c.insured, 0) };
      });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const updateClub = async (c, patch) => {
    try {
      const { data: res } = await api.put("/registration/club", { school: c.school_key, day, ...patch });
      const r = await api.get("/registration", { params: { day } });
      setData(r.data);
      toast.success(`Zaktualizowano ${res.updated} zawodników: ${c.school}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = async () => {
    try {
      await downloadFile({ path: "/registration/export.csv", params: { day }, filename: `lista-obecnosci-${day.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv` });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const clubs = useMemo(() => {
    if (!data) return [];
    const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ł/g, "l");
    const needle = norm(q.trim());
    const match = (p, c) => (!needle || norm(p.name).includes(needle) || norm(c.school || "").includes(needle)) &&
      (!status || status === "Wszyscy" || (status === "Obecni" ? p.present : status === "Nieobecni" ? !p.present : !p.insured));
    return data.clubs
      .filter((c) => !club || c.school === club)
      .map((c) => ({ ...c, shown: c.persons.filter((p) => match(p, c)) }))
      .filter((c) => c.shown.length);
  }, [data, club, status, q]);

  return (
    <div className="mt-5">
      <div className="no-print">
      <DayTabs days={days} value={day} onChange={setDay} prefix="reg-day" />
      {data && (
        <div className="grid grid-cols-3 gap-3 mt-4" data-testid="reg-totals">
          <Stat label="Zawodników" value={data.total} testid="reg-total" />
          <Stat label="Na miejscu" value={data.present} of={data.total} tone="text-emerald-400" testid="reg-present" />
          <Stat label="Ubezpieczeni" value={data.insured} of={data.total} tone="text-cyan-400" testid="reg-insured" />
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-4">
        <div className="relative w-full">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="reg-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj zawodnika lub klubu…"
            className="w-full h-12 pl-9 pr-10 rounded-xl bg-[#1C2541] border border-blue-500/30 focus:border-blue-500 outline-none text-base" />
          {q && <button data-testid="reg-search-clear" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
        <FilterSelect testid="reg-filter-club" className="flex-1 min-w-[220px]" value={club} onChange={setClub} options={(data?.clubs || []).map((c) => c.school)} placeholder="Wszystkie kluby" />
        <FilterSelect testid="reg-filter-status" className="min-w-[200px]" value={status} onChange={setStatus} options={STATUS.slice(1)} placeholder="Wszyscy zawodnicy" />
        <button data-testid="reg-csv-btn" onClick={exportCsv} className="h-11 px-4 rounded-xl bg-[#1C2541] border border-blue-500/20 hover:border-blue-500 text-sm flex items-center gap-2"><Download className="w-4 h-4" />CSV</button>
        <button data-testid="reg-print-btn" onClick={() => window.print()} className="h-11 px-4 rounded-xl bg-[#1C2541] border border-blue-500/20 hover:border-blue-500 text-sm flex items-center gap-2"><Printer className="w-4 h-4" />Drukuj listę</button>
      </div>
      <div className="mt-4 space-y-4">
        {clubs.map((c) => <ClubCard key={c.school} c={c} onUpdate={update} onClub={updateClub} />)}
        {data && clubs.length === 0 && <p className="text-slate-500 text-sm py-10 text-center">Brak zawodników do wyświetlenia.</p>}
      </div>
      </div>
      <PrintList day={day} clubs={clubs} />
    </div>
  );
};

const PrintList = ({ day, clubs }) => (
  <div className="hidden print:block text-black" data-testid="reg-print-list">
    <h1 className="text-2xl font-bold">Lista obecności – {day}</h1>
    {clubs.map((c) => (
      <div key={c.school} className="mt-4 break-inside-avoid">
        <h2 className="font-bold text-lg border-b border-black">{c.school} ({c.present}/{c.persons.length} obecnych)</h2>
        <table className="w-full text-sm">
          <thead><tr className="text-left"><th>Zawodnik</th><th>Kategorie</th><th>Obecny</th><th>Ubezp.</th></tr></thead>
          <tbody>
            {c.shown.map((p) => (
              <tr key={p.name} className="border-b border-gray-300">
                <td className="py-1">{p.name}</td>
                <td className="py-1 text-xs">{p.entries.map((e) => e.category).join(", ")}</td>
                <td className="py-1">{p.present ? "TAK" : "NIE"}</td>
                <td className="py-1">{p.insured ? "TAK" : "NIE"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ))}
  </div>
);

const Stat = ({ label, value, of, tone = "text-white", testid }) => (
  <div className="fly-card p-4">
    <div className="font-mono text-[10px] uppercase tracking-[0.08em] sm:tracking-[0.2em] text-slate-400 break-words">{label}</div>
    <div data-testid={testid} className={`font-display text-4xl font-black ${tone}`}>{value}{of != null && <span className="text-slate-500 text-xl"> / {of}</span>}</div>
  </div>
);

const ClubCard = ({ c, onUpdate, onClub }) => {
  const [open, setOpen] = useState(true);
  const slug = c.school.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const allPresent = c.present === c.persons.length;
  const allInsured = c.insured === c.persons.length;
  return (
    <section className="fly-card overflow-hidden" data-testid={`reg-club-${slug}`}>
      <div className="px-5 py-4 flex items-center gap-3 flex-wrap bg-gradient-to-r from-blue-600/15 to-transparent">
      <button onClick={() => setOpen((v) => !v)} data-testid={`reg-club-toggle-${slug}`} className="flex-1 min-w-[200px] flex items-center justify-between gap-3 text-left">
        <div>
          <h3 className="font-display text-xl sm:text-2xl font-bold uppercase leading-tight">{c.school}</h3>
          <div className="font-mono text-xs text-slate-400 mt-0.5">
            <span className="text-emerald-400">{c.present}</span>/{c.persons.length} na miejscu · <span className="text-cyan-400">{c.insured}</span>/{c.persons.length} ubezp.
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex">
      <button data-testid={`reg-club-all-present-${slug}`} onClick={() => onClub(c, { present: !allPresent })}
        className={`h-10 px-2 sm:px-3 rounded-lg border text-[11px] sm:text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-95 transition-transform ${allPresent ? "border-white/15 text-slate-300 hover:bg-white/5" : "border-emerald-500/60 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20"}`}>
        <CheckCircle2 className="w-4 h-4" />{allPresent ? "Odznacz obecność klubu" : "Cały klub na miejscu"}
      </button>
      <button data-testid={`reg-club-all-insured-${slug}`} onClick={() => onClub(c, { insured: !allInsured })}
        className={`h-10 px-2 sm:px-3 rounded-lg border text-[11px] sm:text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-95 transition-transform ${allInsured ? "border-white/15 text-slate-300 hover:bg-white/5" : "border-cyan-500/60 text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20"}`}>
        <ShieldCheck className="w-4 h-4" />{allInsured ? "Odznacz ubezpieczenie klubu" : "Cały klub ubezpieczony"}
      </button>
      </div>
      </div>
      {open && (
        <ul>
          {c.shown.map((p) => <PersonRow key={p.name} p={p} onUpdate={onUpdate} />)}
        </ul>
      )}
    </section>
  );
};

const PersonRow = ({ p, onUpdate }) => {
  const id = `${p.name}-${p.school}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <li className="border-t border-white/5 px-5 py-3 flex items-center gap-3 flex-wrap" data-testid={`reg-person-${id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="font-semibold">{p.name}</div>
        <div className="text-xs text-slate-400">{p.entries.map((e) => `${e.time ? e.time + " · " : ""}${e.category} (${e.age_group})`).join(" | ")}</div>
      </div>
      <Toggle on={p.present} onClick={() => onUpdate(p, { present: !p.present })} testid={`reg-present-${id}`} icon={CheckCircle2} labelOn="Na miejscu" labelOff="Nieobecny" color="emerald" />
      <Toggle on={p.insured} onClick={() => onUpdate(p, { insured: !p.insured })} testid={`reg-insured-${id}`} icon={ShieldCheck} labelOn="Ubezpieczony" labelOff="Brak ubezp." color="cyan" />
    </li>
  );
};

const COLORS = {
  emerald: "bg-emerald-500/20 border-emerald-500 text-emerald-300",
  cyan: "bg-cyan-500/20 border-cyan-500 text-cyan-300",
};

const Toggle = ({ on, onClick, testid, icon: Icon, labelOn, labelOff, color }) => (
  <button data-testid={testid} aria-pressed={on} onClick={onClick}
    className={`h-12 flex-1 sm:flex-none sm:min-w-[150px] px-3 sm:px-4 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 active:scale-95 transition-[transform,background-color,border-color] ${on ? COLORS[color] : "border-white/10 text-slate-400 hover:border-white/30"}`}>
    <Icon className="w-5 h-5" />{on ? labelOn : labelOff}
  </button>
);
